import { differenceInHours, differenceInMonths } from 'date-fns';
import { TNessBadgeListViewProps, TNessBadgeViewProps } from '../components';
import { isLegacyStockProductData } from '@services/MarppleShop/features/ProductList';

/**
 * @ness-only
 * Ness 뱃지 생성
 *
 * @legacy
 * https://github.com/marpple/marpple-web/blob/0898dfd20d51cad9a7fbb8555fdb95c46a1f06ed/modules/Ness/App/ProductBadge/S/Function/nessProductBadge.js#L15-L82
 *
 * @legacy
 * https://github.com/marpple/marpple-web/blob/bb687feb8891dd5bd415ecda9005075a374fe2b5/modules/Ness/App/ProductBadge/__deprecated/nessProductBadge.js
 */
export const createNessBadges = (data: TNessBadgeListViewProps): TNessBadgeViewProps[] => {
  // rune에서 꺼내온 데이터가 Date 타입이 아니라 string으로 변환되는 이슈 있어서 추가한 코드
  if (data.publicAt) {
    data.publicAt = new Date(data.publicAt);
  }
  if (data.salePeriod.startDate) {
    data.salePeriod.startDate = new Date(data.salePeriod.startDate);
  }
  if (data.salePeriod.endDate) {
    data.salePeriod.endDate = new Date(data.salePeriod.endDate);
  }

  let Custom;
  if (data.isCustom) {
    Custom = {
      type: 'Custom' as const,
    };
  }

  let Rank;
  if (data.rank && data.rank > 0) {
    Rank = {
      type: 'Rank' as const,
      rank: data.rank,
    };
  }

  let Stock_left;

  if (/** MPS, DTG */ isLegacyStockProductData(data)) {
    const MAX_STOCK_FOR_DISPLAY = 99; // 99개 이하만 노출
    const currentDate = new Date();

    // 1. 상품 타입 확인
    const isLegacyProduct = isLegacyStockProductData(data);

    // 2. 판매 기간 확인
    const isSaleStarted = !data.salePeriod.startDate || data.salePeriod.startDate < currentDate;
    const isSaleNotEnded = !data.salePeriod.endDate || currentDate < data.salePeriod.endDate;

    // 3. 화면에 표시할 재고 수량 확인
    const availableStock = data.legacyStock.totalQuantity - data.legacyStock.orderCount;
    const isStockAvailableForDisplay = availableStock > 0 && availableStock <= MAX_STOCK_FOR_DISPLAY;
    const hasStock = !data.legacyStock.isOutOfStock;

    if (isLegacyProduct && isSaleStarted && isSaleNotEnded && isStockAvailableForDisplay && hasStock) {
      if (/** 0 < availableStock < 10 */ availableStock < 10) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 1,
        };
      } /** 10 <= availableStock < 30 */ else if (availableStock < 30) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 2,
        };
      } /** 30 <= availableStock < 50 */ else if (availableStock < 50) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 3,
        };
      } /** 50 <= availableStock < 99 */ else if (availableStock <= MAX_STOCK_FOR_DISPLAY) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 4,
        };
      }
    }
  } /** PBG, TPL */ else {
    const currentDate = new Date();
    const isSaleStarted = !data.salePeriod.startDate || data.salePeriod.startDate < currentDate;
    const isSaleNotEnded = !data.salePeriod.endDate || currentDate < data.salePeriod.endDate;

    if (
      isSaleStarted &&
      isSaleNotEnded &&
      data.lowStockMeta &&
      data.stock.availableStock > 0 &&
      data.stock.availableStock < data.lowStockMeta.threshold
    ) {
      Stock_left = {
        type: 'Stock_left' as const,
        availableStock: data.stock.availableStock,
        priority: 1,
      };
    }
  }

  let Time_left;
  /** MPS, DTG */ if (isLegacyStockProductData(data)) {
    const currentDate = new Date();
    const isSaleStarted = !data.salePeriod.startDate || data.salePeriod.startDate < currentDate;

    if (
      isSaleStarted &&
      data.salePeriod.endDate &&
      new Date() < data.salePeriod.endDate && // 판매 종료일이 미래
      !data.legacyStock.isOutOfStock
    ) {
      const hoursDiff = differenceInHours(data.salePeriod.endDate, new Date());

      if (hoursDiff < 168) {
        if (/** 96 <= hoursDiff < 168 */ hoursDiff >= 96) {
          Time_left = {
            type: 'Time_left' as const,
            endDate: data.salePeriod.endDate,
            priority: 5,
          };
        } /** 48 <= hoursDiff < 96 */ else if (hoursDiff >= 48) {
          Time_left = {
            type: 'Time_left' as const,
            endDate: data.salePeriod.endDate,
            priority: 4,
          };
        } /** 24 <= hoursDiff < 48 */ else if (hoursDiff >= 24) {
          Time_left = {
            type: 'Time_left' as const,
            endDate: data.salePeriod.endDate,
            priority: 3,
          };
        } /** 2 < hoursDiff < 24 */ else if (hoursDiff > 2) {
          Time_left = {
            type: 'Time_left' as const,
            endDate: data.salePeriod.endDate,
            priority: 2,
          };
        } /** hoursDiff <= 2 */ else {
          Time_left = {
            type: 'Time_left' as const,
            endDate: data.salePeriod.endDate,
            priority: 1,
          };
        }
      }
    }
  } /** PBG, TPL */ else {
    if (data.salePeriod.endDate && data.salePeriod.endDate > new Date()) {
      Time_left = {
        type: 'Time_left' as const,
        endDate: data.salePeriod.endDate,
        priority: 1,
      };
    }
  }

  let Special_offer;
  if (/** MPS, DTG */ isLegacyStockProductData(data)) {
    const isQuantityLimit = data.legacyStock.totalQuantity > 0; // 한정이 설정됨
    const isPeriodLimit = !!data.salePeriod.endDate; // 종료일이 설정됨

    if (isQuantityLimit || isPeriodLimit) {
      Special_offer = {
        type: 'Special_offer' as const,
      };
    }
  } /** PBG, TPL */ else {
    if (data.limitedMeta?.isLimited) {
      Special_offer = {
        type: 'Special_offer' as const,
      };
    }
  }

  let Sale;
  if (data.discountPercentage && data.discountPercentage > 0) {
    Sale = {
      type: 'Sale' as const,
      discountPercentage: data.discountPercentage,
    };
  }

  let New;
  if (data.publicAt) {
    const monthsDiff = differenceInMonths(new Date(), data.publicAt);
    if (monthsDiff < 1) {
      New = {
        type: 'New' as const,
        publicAt: data.publicAt,
      };
    }
  }

  if (Custom) {
    return [Custom];
  }

  if (Rank && Special_offer) {
    return [Rank, Special_offer];
  }
  if (Rank && Sale) {
    return [Rank, Sale];
  }
  if (Rank) {
    return [Rank];
  }

  // Stock_left, Time_left 모두 존재
  if (Special_offer && Stock_left && Time_left) {
    // Stock_left가 우선순위 높음 (낮은 값이 높은 우선순위)
    if (Stock_left.priority <= Time_left.priority) {
      return [Special_offer, Stock_left];
    } else {
      return [Special_offer, Time_left];
    }
  }

  if (Special_offer && Stock_left) {
    return [Special_offer, Stock_left];
  }
  if (Special_offer && Time_left) {
    return [Special_offer, Time_left];
  }
  if (Special_offer && Sale) {
    return [Special_offer, Sale];
  }
  if (Special_offer && New) {
    return [Special_offer, New];
  }
  if (Special_offer) {
    return [Special_offer];
  }

  if (Sale && Stock_left && Time_left) {
    if (Stock_left.priority <= Time_left.priority) {
      return [Sale, Stock_left];
    } else {
      return [Sale, Time_left];
    }
  }
  if (Sale && Stock_left) {
    return [Sale, Stock_left];
  }
  if (Sale && Time_left) {
    return [Sale, Time_left];
  }

  if (Sale && New) {
    return [Sale, New];
  }

  if (New && Stock_left && Time_left) {
    if (Stock_left.priority <= Time_left.priority) {
      return [New, Stock_left];
    } else {
      return [New, Time_left];
    }
  }
  if (New && Stock_left) {
    return [New, Stock_left];
  }
  if (New && Time_left) {
    return [New, Time_left];
  }

  if (Stock_left && Time_left) {
    if (Stock_left.priority <= Time_left.priority) {
      return [Stock_left];
    } else {
      return [Time_left];
    }
  }

  if (Stock_left) {
    return [Stock_left];
  }

  if (Time_left) {
    return [Time_left];
  }

  if (Sale) {
    return [Sale];
  }

  if (New) {
    return [New];
  }

  return [];
};
